import React, { useEffect, useRef, useState } from 'react'
import { ErrorOutline, Info, Warning, iconSize } from 'components/icons'
import Tooltip, { TOOLTIP_CLOSE, TOOLTIP_OPEN } from 'components/tooltip'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'

// A note on a cell, flush right: a value that breaks its column's validation, or why a matrix cell that more than
// one row lands in is blank. Its message shows while the pointer is on the mark and while the cell is selected, so
// clicking a locked cell, or arrowing onto it, says why it can't be edited. The cell names the message as its
// description (``descriptionId``), so a screen reader reads it out on the cell that has focus.

/** Each tone has its own shape as well as its colour, so they tell apart without seeing colour. */
export const MARKS = {
  /** A rule that blocks the value is broken. */
  error: { Icon: ErrorOutline, className: 'text-red' },
  /** A rule is broken that only flags the value, which stands. */
  warning: { Icon: Warning, className: 'text-orange' },
  /** Nothing is wrong: why the cell shows what it does. */
  info: { Icon: Info, className: 'text-muted' }
}

export type CellIssueTone = keyof typeof MARKS

/** Frames the mark must stay put, after moving, before its tooltip shows again. */
const SETTLED_FRAMES = 3

interface CellIssueProps {
  message: string
  tone: CellIssueTone
  /** The id the cell's aria-describedby points at. */
  descriptionId: string
  /** The cell is selected and not being edited. */
  selected: boolean
  rowHeight: number
}

export const CellIssue: React.FC<CellIssueProps> = ({ message, tone, descriptionId, selected, rowHeight }) => {
  const markRef = useRef<HTMLSpanElement>(null)
  const [hovered, setHovered] = useState(false)
  // Put away by Escape or the window losing focus, until the cell is selected again.
  const [dismissed, setDismissed] = useState(false)
  // The tooltip is placed once, where the mark is as it shows. While the mark moves (the grid scrolling, which it
  // does without scroll events, or bringing a cell arrowed onto into view) it hides, and is placed afresh where the
  // mark comes to rest, rather than left behind; while the mark is out of sight (under the header, say) it stays hidden.
  const [placing, setPlacing] = useState(0)
  const [moving, setMoving] = useState(false)
  const [outOfSight, setOutOfSight] = useState(false)
  // Another tooltip the pointer brought up (another cell's mark, a button's): the selected cell's message gives way
  // to it, so only one shows.
  const [givenWayTo, setGivenWayTo] = useState<string | null>(null)
  const [wasSelected, setWasSelected] = useState(selected)
  if (selected !== wasSelected) {
    setWasSelected(selected)
    setDismissed(false)
  }
  const wanted = hovered || (selected && !dismissed)
  const open = wanted && !moving && !outOfSight && (hovered || givenWayTo === null)
  const { Icon, className } = MARKS[tone]

  useEffect(() => {
    if (!wanted) return
    const dismiss = () => {
      setHovered(false)
      setDismissed(true)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss()
    }
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('blur', dismiss)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('blur', dismiss)
    }
  }, [wanted])

  useEffect(() => {
    if (!open) return
    // As the app's hover tooltips do, however this one opened (a hover, or the cell selected), so they make way for
    // it and it for them.
    document.dispatchEvent(new CustomEvent(TOOLTIP_OPEN, { detail: descriptionId }))
    return () => {
      document.dispatchEvent(new CustomEvent(TOOLTIP_CLOSE, { detail: descriptionId }))
    }
  }, [open, descriptionId])

  useEffect(() => {
    if (!selected) return
    const onOpen = (event: Event) => {
      const id = (event as CustomEvent<string>).detail
      if (id !== descriptionId) setGivenWayTo(id)
    }
    const onClose = (event: Event) => {
      const id = (event as CustomEvent<string>).detail
      setGivenWayTo((current) => (current === id ? null : current))
    }
    document.addEventListener(TOOLTIP_OPEN, onOpen)
    document.addEventListener(TOOLTIP_CLOSE, onClose)
    return () => {
      document.removeEventListener(TOOLTIP_OPEN, onOpen)
      document.removeEventListener(TOOLTIP_CLOSE, onClose)
      setGivenWayTo(null)
    }
  }, [selected, descriptionId])

  useEffect(() => {
    if (!wanted) return
    let frame = 0
    let last: DOMRect | undefined
    // Frames the mark has stayed put since it last moved; null while it hasn't moved.
    let still: number | null = null
    const watch = () => {
      const mark = markRef.current
      const now = mark?.getBoundingClientRect()
      const moved = !!last && !!now && (now.top !== last.top || now.left !== last.left)
      if (moved) {
        // The pointer is no longer where it was over the mark.
        setHovered(false)
        setMoving(true)
        still = 0
      } else if (still !== null) {
        still = still + 1
        if (still >= SETTLED_FRAMES) {
          still = null
          setPlacing((count) => count + 1)
          setMoving(false)
        }
      }
      if (mark && now && (!last || moved)) {
        const middle = document.elementFromPoint?.(now.left + now.width / 2, now.top + now.height / 2)
        setOutOfSight(!!document.elementFromPoint && (!middle || !mark.contains(middle)))
      }
      last = now
      frame = window.requestAnimationFrame(watch)
    }
    frame = window.requestAnimationFrame(watch)
    return () => {
      window.cancelAnimationFrame(frame)
      // Stopped mid-move, it would otherwise stay hidden the next time it's wanted.
      setMoving(false)
    }
  }, [wanted])

  return (
    <Tooltip key={placing} content={message} visible={open} className="max-w-[280px]">
      <div
        // In from the right as a cell's content is from either side.
        className="absolute right-[8px] flex"
        style={{ top: getRowHeightVariable(rowHeight) / 2 - iconSize / 2 }}
        data-testid="cell-issue"
        data-tone={tone}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span ref={markRef} className="flex">
          <Icon className={className} />
        </span>
        {/* Hidden, so it isn't part of the cell's text; a description is read from hidden elements too. */}
        <span id={descriptionId} hidden>
          {message}
        </span>
      </div>
    </Tooltip>
  )
}
